import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { countryName } from "./utils/country";

const WORLD_ATLAS_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const NAME_ALIASES: Record<string, string> = {
  "united states": "united states of america",
  tanzania: "united republic of tanzania",
  russia: "russian federation",
  "south korea": "korea, republic of",
  "north korea": "dem. rep. korea",
  "ivory coast": "côte d'ivoire",
  "democratic republic of the congo": "dem. rep. congo",
  laos: "lao pdr",
};

export interface CountryViewsDatum {
  code: string;
  views: number;
}

interface CountryViewsMapCardProps {
  data: CountryViewsDatum[];
}

type GeographyFeature = {
  rsmKey?: string;
  properties?: {
    name?: string;
  };
};

type GeographyRenderData = {
  geographies: GeographyFeature[];
};

const EMPTY_COLOR = "#E9EDF3";
const MIN_COLOR = [199, 230, 192];
const MAX_COLOR = [90, 178, 81];

function interpolateColor(ratio: number): string {
  const [r1, g1, b1] = MIN_COLOR;
  const [r2, g2, b2] = MAX_COLOR;
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function CountryViewsMapCard({
  data,
}: CountryViewsMapCardProps) {
  const [hovered, setHovered] = useState<{
    name: string;
    views: number;
  } | null>(null);

  const isEmpty = data.length === 0 || data.every((item) => item.views === 0);
  const maxViews = Math.max(...data.map((item) => item.views), 1);

  const viewsByName = useMemo(() => {
    const map = new Map<string, { name: string; views: number }>();
    data.forEach((item) => {
      const name = countryName(item.code);
      const key = (NAME_ALIASES[name.toLowerCase()] || name).toLowerCase();
      map.set(key, { name, views: item.views });
    });
    return map;
  }, [data]);

  const topCountry = useMemo(() => {
    if (data.length === 0) return null;
    const top = [...data].sort((a, b) => b.views - a.views)[0];
    return { name: countryName(top.code), views: top.views };
  }, [data]);

  const displayed = hovered || topCountry;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 0 }}>
        <CardContent>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Views By Country
            </Typography>
            {displayed && !isEmpty && (
              <Typography variant="body2" color="text.secondary">
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {displayed.name}
                </Box>{" "}
                · {displayed.views.toLocaleString()} views
              </Typography>
            )}
          </Box>

          {isEmpty ? (
            <Box
              sx={{
                height: 260,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No location report
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 260 }}>
              <ComposableMap
                projectionConfig={{ scale: 130 }}
                width={800}
                height={400}
                style={{ width: "100%", height: "100%" }}
              >
                <Geographies geography={WORLD_ATLAS_URL}>
                  {({ geographies }: GeographyRenderData) =>
                    geographies.map((geo: GeographyFeature) => {
                      const rawName: string = geo.properties?.name || "";
                      const match = viewsByName.get(rawName.toLowerCase());
                      const fill = match
                        ? interpolateColor(match.views / maxViews)
                        : EMPTY_COLOR;

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() =>
                            match &&
                            setHovered({
                              name: match.name,
                              views: match.views,
                            })
                          }
                          onMouseLeave={() => setHovered(null)}
                          style={{
                            default: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            hover: {
                              fill: match ? "#3F8C38" : EMPTY_COLOR,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                              cursor: match ? "pointer" : "default",
                            },
                            pressed: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
